/**
 * What a tenant gets when NOTHING says otherwise (0177).
 *
 * These are the inherited base levels for a tenant with no override, and — on a
 * deployment where plans are not meaningful — the inherited base levels full
 * stop. One module, because the projector and each enforcer must agree: a
 * default that lives in two places is a screen showing a limit the gate is not
 * applying.
 *
 * ── Why `null` is not among them ────────────────────────────────────────
 *
 * Every value here is CONCRETE. The concurrency axis is the cautionary tale:
 * `billing_plans.agent_concurrency_cap` is nullable, the entitlement combinator
 * documented that `null` as "unlimited", and it fed it through a `max()` whose
 * `null` absorbs everything — including additive promo boosts. But the actual
 * enforcer has always read `null` as `DEFAULT_AGENT_CONCURRENCY_CAP`, i.e. 3.
 *
 * So a promo granting "+2 concurrent jobs" to a tenant on a plan with no
 * explicit cap produced `null`, which the enforcer read as 3 — the boost
 * silently discarded, the customer's grant never delivered, and nothing
 * anywhere in error. Resolving to a concrete number BEFORE the arithmetic is
 * what makes a boost land.
 */

/** Free-tier storage when no plan and no override say otherwise. 5 GiB. */
export const DEFAULT_STORAGE_QUOTA_BYTES = 5_368_709_120

/**
 * Concurrent agent jobs per user when nothing says otherwise.
 *
 * Read by `checkAgentConcurrencyCap` (the enforcer) and by the entitlement
 * projector, which is the pairing that matters: the projector resolves this to
 * a number and writes it, so the enforcer's own `?? DEFAULT` is a backstop for
 * rows that predate projection rather than a second opinion.
 */
export const DEFAULT_AGENT_CONCURRENCY_CAP = 3

/**
 * The free allowance when nothing says otherwise: UNLIMITED.
 *
 * `null` is the unlimited spelling on this axis and is a real product state —
 * unlike the extra axis below, where it would be a fail-open. A deployment that
 * has not opted into billing must not have its drives' AI silently hard-blocked
 * because no plan granted a pool, so the absence of a plan means "no ceiling",
 * not "zero ceiling". (`0` here would read as AI blocked for every tenant.)
 */
export const DEFAULT_FREE_CAP_BILLING_MICRO: number | null = null

/**
 * Postpaid overage when nothing says otherwise: DISABLED.
 *
 * The opposite direction from `free` above, and deliberately so. Free is money
 * the operator has already decided to give away; extra is money the customer
 * is billed for after the fact. Defaulting an unstated billable axis to "on"
 * is how an absent value became `+Infinity` at the enforcer and admitted
 * unbounded spend.
 */
export const DEFAULT_EXTRA_CAP_BILLING_MICRO = 0

/**
 * `int4` ceiling. `tenants.agent_concurrency_cap` is `integer`, so an effective
 * value above this cannot be stored — and Postgres raises rather than
 * truncating, which would fail the projection of an otherwise valid override.
 */
export const MAX_INT4 = 2_147_483_647
